import React, { FC, useMemo } from "react";
import { Pressable, View } from "react-native";
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from "react-native-reanimated";
import { useI18nMessage } from "@lookiero/i18n-react";
import { Button, Icon, Text } from "@lookiero/sty-psp-ui";
import { CheckoutPricingProjection } from "../../../../../../projection/pricing/pricing";
import { Price } from "../../../../components/atoms/price/Price";
import { DOMAIN, I18nMessages } from "../../../../i18n/i18n";
import { Pricing } from "../pricing/Pricing";
import { style as collapsiblePricingStyle } from "./CollapsiblePricing.style";

interface CollapsiblePricingProps {
  readonly pricing: CheckoutPricingProjection;
  readonly totalCheckoutItemsKept: number;
  readonly collapsible?: boolean;
  readonly collapsed?: boolean;
  readonly submitButtonText: string;
  readonly onPress?: () => void;
  readonly onSubmit: () => void;
}
const CollapsiblePricing: FC<CollapsiblePricingProps> = ({
  pricing,
  totalCheckoutItemsKept,
  collapsible = true,
  collapsed = false,
  submitButtonText,
  onPress = () => void 0,
  onSubmit,
}) => {
  const { finalPrice, currency } = pricing;

  const totalText = useI18nMessage({ domain: DOMAIN, id: I18nMessages.SUMMARY_TOTAL });
  const totalCheckoutItemsKeptText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.SUMMARY_TOTAL_ITEMS_KEPT,
    values: { items: totalCheckoutItemsKept.toString() },
  });

  const collapsedOpacity = useSharedValue(collapsed ? 1 : 0);
  collapsedOpacity.value = collapsed ? 1 : 0;
  const collapsedAnimatedStyle = useAnimatedStyle(
    () => ({ opacity: withTiming(collapsedOpacity.value) }),
    [collapsedOpacity.value],
  );
  const notCollapsedOpacity = useSharedValue(collapsed ? 0 : 1);
  notCollapsedOpacity.value = collapsed ? 0 : 1;
  const notCollapsedAnimatedStyle = useAnimatedStyle(
    () => ({ opacity: withTiming(notCollapsedOpacity.value) }),
    [notCollapsedOpacity.value],
  );

  const style = useMemo(() => collapsiblePricingStyle(), []);

  return (
    <>
      {collapsible && (
        <Pressable style={style.iconContainer} testID="pricing-collapsed" onPress={onPress}>
          {collapsed ? (
            <Icon name="arrow_up_small" testID="arrow-up" />
          ) : (
            <Icon name="arrow_down_small" testID="arrow-down" />
          )}
        </Pressable>
      )}

      {collapsed && collapsible ? (
        <Animated.View key="collapsed" style={[style.collapsed, collapsedAnimatedStyle]}>
          <View style={style.collapsedContent}>
            <Text level={1} style={style.totalCollapsed} variant="detail">
              {totalText} {totalCheckoutItemsKeptText}
            </Text>
            <Price amount={finalPrice} currency={currency} variant="detail" />
          </View>

          <View style={style.collapsedContent}>
            <Button size="small" testID="submit-checkout-collpased-button" onPress={onSubmit}>
              {submitButtonText}
            </Button>
          </View>
        </Animated.View>
      ) : (
        <Animated.View key="not-collapsed" style={notCollapsedAnimatedStyle}>
          <Pricing pricing={pricing} totalCheckoutItemsKept={totalCheckoutItemsKept} />

          <Button testID="submit-checkout-button" onPress={onSubmit}>
            {submitButtonText}
          </Button>
        </Animated.View>
      )}
    </>
  );
};

export { CollapsiblePricing };
